/**
 * MobileIftShell — IFT-only mobile Web-App shell.
 *
 * Redesigned Oct 2026 to match the new mobile home spec:
 *   • Top bar: brand lockup (logo + "IndianFamilyTree.com" + tagline),
 *     search icon, hamburger (links to /m/more).
 *   • Bottom nav: 6 items — Home · Family Trees · Memorials · Search
 *     · Explore · Sign In/Me.
 *
 * The shell never replaces the desktop layout — it renders alongside
 * <Header> / <Footer> in App.js, gated by MobileIftGate.
 */
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home as HomeIcon,
  TreePine as TreeIcon,
  Flame as MemorialIcon,
  Search as SearchIcon,
  Globe as ExploreIcon,
  UserCircle2 as MeIcon,
  Search,
  Menu,
} from "lucide-react";

const P = {
  cream:        "#FEF9F0",
  parchment:    "#FAF6EE",
  ink:          "#3B2E1F",
  inkSoft:      "#6B5A3F",
  green:        "#15803D",
  greenDeep:    "#166534",
  saffron:      "#C2410C",
  gold:         "#D97706",
  line:         "rgba(59,46,31,0.10)",
  tileBorder:   "rgba(59,46,31,0.08)",
};

const NAV = [
  { to: "/m",            end: true, label: "Home",         Icon: HomeIcon,     testid: "mnav-home" },
  { to: "/m/tree",                  label: "Family Trees", Icon: TreeIcon,     testid: "mnav-tree" },
  { to: "/celebrate",               label: "Memorials",    Icon: MemorialIcon, testid: "mnav-memorials" },
  { to: "/search",                  label: "Search",       Icon: SearchIcon,   testid: "mnav-search" },
  { to: "/m/people",                label: "Explore",      Icon: ExploreIcon,  testid: "mnav-explore" },
  // Last slot flips between Sign In (anon) and Me (signed-in) at runtime
];

export function MobileIftTopBar() {
  const nav = useNavigate();
  return (
    <header
      className="sticky top-0 z-30 flex items-center justify-between px-4 py-3"
      style={{ background: P.cream, borderBottom: `1px solid ${P.line}` }}
      data-testid="ift-mtopbar"
    >
      <Link to="/m" className="flex items-center gap-2 min-w-0" data-testid="ift-mtopbar-logo">
        <img
          src="/assets/ift-logo.png"
          alt=""
          width={38} height={38}
          style={{ borderRadius: 8, flexShrink: 0 }}
          onError={(e) => { e.currentTarget.src = "/ift/icons/icon-192.png"; }}
        />
        <span className="flex flex-col leading-tight min-w-0">
          <span className="text-[15.5px] font-bold truncate" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            <span style={{ color: P.saffron }}>Indian</span>
            <span style={{ color: P.green }}>FamilyTree</span>
            <span style={{ color: P.ink }}>.com</span>
          </span>
          <span className="text-[8.5px] font-mono uppercase tracking-[0.18em] truncate" style={{ color: P.inkSoft }}>
            People · Heritage · Memories · Forever
          </span>
        </span>
      </Link>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={() => nav("/search")}
          aria-label="Search"
          data-testid="ift-mtopbar-search"
          className="w-10 h-10 rounded-full flex items-center justify-center active:bg-black/5"
          style={{ color: P.ink }}
        >
          <Search className="w-5 h-5" />
        </button>
        <button
          onClick={() => nav("/m/more")}
          aria-label="Menu"
          data-testid="ift-mtopbar-menu"
          className="w-10 h-10 rounded-full flex items-center justify-center active:bg-black/5"
          style={{ color: P.ink }}
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>
    </header>
  );
}

export function MobileIftBottomNav() {
  const { user } = useAuth();
  const lastItem = user
    ? { to: "/dashboard", label: "Me",      Icon: MeIcon, testid: "mnav-me" }
    : { to: "/m/login",   label: "Sign In", Icon: MeIcon, testid: "mnav-signin" };
  const items = [...NAV, lastItem];
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-30"
      style={{
        background: "rgba(254,249,240,0.97)",
        borderTop: `1px solid ${P.line}`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="ift-mbottomnav"
    >
      <ul className="flex items-stretch justify-around">
        {items.map(({ to, end, label, Icon, testid }) => (
          <li key={testid} className="flex-1 min-w-0">
            <NavLink
              to={to}
              end={end}
              data-testid={testid}
              className="flex flex-col items-center justify-center py-2 gap-0.5 min-w-0"
              style={({ isActive }) => ({
                color: isActive ? P.green : P.inkSoft,
              })}
            >
              {({ isActive }) => (
                <>
                  <Icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.3 : 1.8} />
                  <span className="text-[9.5px] font-semibold leading-none truncate max-w-full">{label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 w-6 h-0.5 rounded-full" style={{ background: P.green }} />
                  )}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Wraps its children in the IFT mobile chrome. Consumers render either
 * MobileIftShell (with Outlet-like children) or a single-page component.
 */
export default function MobileIftShell({ children }) {
  return (
    <div
      style={{ minHeight: "100vh", background: P.parchment, paddingBottom: 92 }}
      data-testid="ift-mshell"
    >
      <MobileIftTopBar />
      <main>{children}</main>
      <MobileIftBottomNav />
    </div>
  );
}

/**
 * Detects if the IFT mobile shell should hijack the layout for the
 * current viewport. Returns a boolean; the /m/* routes always render
 * the shell regardless of viewport (so /m/tree from desktop still works
 * for QA), but the auto-redirect only fires for narrow viewports.
 */
export function useShouldUseIftMobile() {
  if (typeof window === "undefined") return false;
  try { return window.matchMedia("(max-width: 767px)").matches; } catch { return false; }
}
